import { fetchPushKey, subscribePush, unsubscribePush } from "./api";
import type { WebPushSubscription } from "@shared/types/foyer";

/**
 * push.ts - the relative PWA's Web Push registration (VC-23).
 *
 * The BACKGROUND ring: while `App`'s foreground poll only fires when the page
 * is open, a push wakes the phone when the app is closed. It is the preferred
 * channel on Android; on iOS it works ONLY once the site is added to the home
 * screen (see docs/video-calling-push.md), which is exactly what the onboarding
 * prompt asks for.
 *
 * Everything here is defensive: an older phone with no `PushManager`, a denied
 * permission, a service worker that never activates - none of them may throw
 * into the call UI. The whole feature degrades to "the SMS still comes"
 * (VC-24), which is the product invariant: the link is the front door, push is
 * an upgrade on top of it, never a gate in front of it.
 */

/**
 * base64url VAPID key → the Uint8Array `applicationServerKey` wants.
 *
 * The `<ArrayBuffer>` argument is load-bearing: bare `Uint8Array` widens to
 * `Uint8Array<ArrayBufferLike>`, which the DOM's `BufferSource` rejects because
 * it could be backed by a `SharedArrayBuffer`. `new Uint8Array(n)` never is.
 */
function urlB64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Serialise a browser PushSubscription to the wire shape the server stores. */
function toWire(sub: PushSubscription): WebPushSubscription | null {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

/** Is Web Push even possible on this device/browser? */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Is the app running as an installed PWA? On iOS this is the precondition for
 * push existing at all, so the onboarding copy leads with "add to home screen"
 * there. `standalone` is the iOS Safari signal; `display-mode` covers the rest.
 */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (
    window.navigator as Navigator & { standalone?: boolean }
  ).standalone;
  return (
    iosStandalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true
  );
}

export type PushState = "granted" | "denied" | "default" | "unsupported";

/** The current notification permission, without prompting. */
export function pushPermission(): PushState {
  if (!pushSupported()) return "unsupported";
  return Notification.permission as PushState;
}

/**
 * Ask for permission (if not already decided) and register a subscription with
 * the server. Returns the resulting state. Never throws - any failure resolves
 * to a state the caller can render, because the SMS path is the safety net.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  try {
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return permission as PushState;

    const reg = await navigator.serviceWorker.ready;
    const { publicKey } = await fetchPushKey();
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      }));

    const wire = toWire(sub);
    if (!wire) return "default";
    await subscribePush(wire, deviceLabel());
    return "granted";
  } catch {
    // A subscribe that failed leaves the SMS path intact; surface it as
    // "not enabled" rather than an error the relative can do nothing about.
    return pushSupported()
      ? (Notification.permission as PushState)
      : "unsupported";
  }
}

/** Unregister this device from the background ring. Best-effort. */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    if (endpoint) await unsubscribePush(endpoint).catch(() => {});
  } catch {
    /* ignore - nothing the relative needs to see */
  }
}

/**
 * Keep the server's subscription fresh on every open. A push endpoint can be
 * rotated by the browser silently; re-registering an already-granted
 * subscription costs nothing and heals that drift, so a device that was rung
 * successfully last week is still reachable today. No prompt, no UI.
 */
export async function refreshSubscription(): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const wire = toWire(sub);
    if (wire) await subscribePush(wire, deviceLabel()).catch(() => {});
  } catch {
    /* ignore */
  }
}

/** A friendly, non-identifying device label for the CMS list ("iPhone"). */
function deviceLabel(): string {
  const ua = navigator.userAgent || "";
  if (/iphone/i.test(ua)) return "iPhone";
  if (/ipad/i.test(ua)) return "iPad";
  if (/android/i.test(ua)) return "Android";
  return "Telefon";
}
